import type {
  CreateRunRequest,
  Run,
  RunStatus,
  RunSummary,
  NormalizedRunPreview,
  RunPreviewRequest,
  PreviewRunStart,
  PreviewContinuation,
  PreviewContinuationMutation,
  CloudUsage,
} from "./runs";
import {
  isTerminalRunStatus,
  normalizePreviewResponse,
  normalizePreviewRunStart,
  normalizePreviewContinuation,
  normalizeRunResponse,
  normalizeRunList,
  normalizeCloudUsage,
} from "./runs";
import { analyticsIdentityHeaders } from "@/analytics/client";
import { accountApiBase, fetchWithConnectionAuth } from "../app/connection";

/**
 * Convert an internal Store ref into the API's preview `source` object. The
 * canonical /v1/runs/preview accepts `{ source: { provider, url } }` (or an
 * `app_id`); we send the object form and detect the provider from the ref.
 */
function toApiSource(ref: string): { provider: string; url: string } {
  if (/^capsule:\/\//i.test(ref)) return { provider: "capsule-url", url: ref };
  if (/^https?:\/\/(www\.)?github\.com\//i.test(ref)) return { provider: "github", url: ref };
  return { provider: "ato-store", url: ref };
}

/** The run-surface's shared sleep — import this instead of adding another
 *  private copy (openCapsuleClient/ManagedCloudDemo predate it). */
export function sleep(ms: number): Promise<void> {
  return new Promise<void>((resolve) => setTimeout(resolve, ms));
}

/**
 * Resolve the public Ato API base. Shares the env var used by the entitlement
 * and checkout clients so the whole account/run surface points at one origin.
 */
function defaultBaseUrl(): string {
  return accountApiBase();
}

export class RunAuthError extends Error {
  readonly status = 401;
  constructor() {
    super("Run API: not signed in (401). Sign in to your Ato account.");
    this.name = "RunAuthError";
  }
}

export class RunConnectionError extends Error {
  constructor(cause?: unknown) {
    super(
      `Run API: could not connect. ${cause instanceof Error ? cause.message : String(cause)}`,
    );
    this.name = "RunConnectionError";
  }
}

export class RunProtocolError extends Error {
  constructor(cause?: unknown) {
    super(
      `Run API: unexpected response format. ${cause instanceof Error ? cause.message : String(cause)}`,
    );
    this.name = "RunProtocolError";
  }
}

export class RunApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    /** The raw error body — typed flows (e.g. the paid-session 402) read
     *  structured fields (checkout_url, paid_claim) from it. */
    readonly body?: unknown,
  ) {
    super(message);
    this.name = "RunApiError";
  }
}

/**
 * Client for the public managed Run API.
 *
 * Security & boundary notes:
 *   - Auth is the account *cookie session* (`credentials: "include"`). It never
 *     sends a Runtime bearer token.
 *   - It only ever calls `/v1/runs*` on the public API origin. It must never
 *     touch the DesktopRuntime control plane (`/v1/runtime/*`). That separation
 *     is what keeps Store "Get & Run" independent of a connected desktop.
 */
export class RunsApiClient {
  private readonly baseUrl: string;
  private readonly fetchImpl: typeof fetch;

  constructor(config: { baseUrl?: string; fetchImpl?: typeof fetch } = {}) {
    this.baseUrl = (config.baseUrl ?? defaultBaseUrl()).replace(/\/$/, "");
    this.fetchImpl = config.fetchImpl ?? fetch.bind(globalThis);
  }

  private async request<T>(
    path: string,
    method: "GET" | "POST",
    body?: unknown,
    extraHeaders?: Record<string, string>,
    init?: { keepalive?: boolean },
  ): Promise<T> {
    let res: Response;
    try {
      res = await fetchWithConnectionAuth(`${this.baseUrl}${path}`, {
        method,
        keepalive: init?.keepalive,
        headers: {
          ...(body === undefined ? {} : { "Content-Type": "application/json" }),
          ...analyticsIdentityHeaders(),
          ...(extraHeaders ?? {}),
        },
        body: body === undefined ? undefined : JSON.stringify(body),
      }, this.fetchImpl);
    } catch (err) {
      throw new RunConnectionError(err);
    }

    if (res.status === 401) throw new RunAuthError();

    let json: unknown;
    try {
      json = await res.json();
    } catch (err) {
      throw new RunProtocolError(err);
    }

    if (!res.ok) {
      const j = json as Record<string, string> | undefined;
      const code = j?.["code"] ?? j?.["error"] ?? "run_api_error";
      const message = j?.["message"] ?? `HTTP ${res.status}`;
      throw new RunApiError(res.status, code, message, json);
    }

    return json as T;
  }

  /** POST /v1/runs/preview — resolve placement and surface gates/blockers. */
  async previewRun(req: RunPreviewRequest): Promise<NormalizedRunPreview> {
    const raw = await this.request<unknown>("/v1/runs/preview", "POST", {
      source: toApiSource(req.source),
      placement: req.placement,
      client: req.client,
    });
    return normalizePreviewResponse(raw);
  }

  /**
   * POST /v1/runs — create a run. Identifies the capsule by `preview_id` when a
   * prior preview is available, else by `app_id`.
   *
   * /v1/runs is a *client-aware* start: the placement (including "auto") is sent
   * verbatim and the same client hints as the preview are included, so the
   * server runs the identical placement selector. We deliberately do NOT rewrite
   * "auto" to "cloud" — that would steer the start onto a legacy explicit-cloud
   * path and drop the desktop-companion signal the server needs to return a
   * `needs_desktop_companion` blocker for local runs.
   */
  async createRun(req: CreateRunRequest): Promise<Run> {
    const body: Record<string, unknown> = {
      placement: req.placement,
      client: req.client,
    };
    if (req.runnerId) body.runner_id = req.runnerId;
    if (req.previewId) body.preview_id = req.previewId;
    else body.app_id = req.source;
    const raw = await this.request<unknown>("/v1/runs", "POST", body);
    return normalizeRunResponse(raw);
  }

  /**
   * POST /v1/preview-runs — launch a PUBLIC Snapshot-Ready app on the login-free
   * free_preview lane (ato#1006, C1/C7). Anonymous BY CONSTRUCTION: no account is
   * required; the server issues/reads the opaque `ato_preview_sid` cookie (carried
   * by `credentials: "include"`), creates an OWNER-LESS run, and mints a
   * time-limited `restore_snapshot_preview` lease. `capsuleRef` is the Store ref
   * (publisher/slug or ULID). When the lane cannot mint a lease (busy / no longer
   * eligible) the server returns only a fresh `offer` (runId null) — NEVER a
   * managed-cloud fallback. This never touches /v1/launches or the /web-runs lane.
   */
  async startPreviewRun(
    capsuleRef: string,
    opts: { paidSession?: boolean; paidClaim?: string | null; origin?: string } = {},
  ): Promise<PreviewRunStart> {
    const raw = await this.request<unknown>("/v1/preview-runs", "POST", {
      capsule_ref: capsuleRef,
      // Anonymous paid session (billing v2 S5): ask for the 1-hour paid lane
      // (server 402s with a guest Checkout URL + claim) / present a paid claim.
      ...(opts.paidSession ? { paid_session: true } : {}),
      ...(opts.paidClaim ? { paid_claim: opts.paidClaim } : {}),
      ...(opts.origin ? { origin: opts.origin } : {}),
    });
    return normalizePreviewRunStart(raw);
  }

  /** GET /v1/runs/:id — fetch current run state for polling. */
  async getRun(id: string): Promise<Run> {
    const raw = await this.request<unknown>(`/v1/runs/${encodeURIComponent(id)}`, "GET");
    return normalizeRunResponse(raw);
  }

  /**
   * GET /v1/account/cloud-usage — the account's monthly cloud usage as a
   * PERCENTAGE (never raw credits). Mock-first + quiet-degrading: returns null
   * when the endpoint is absent (404), returns an unparseable body, or the
   * connection fails — the UI then renders no meter until the backend ships.
   * Auth errors still propagate (signed-out is a real state, not "no usage").
   */
  async getCloudUsage(): Promise<CloudUsage | null> {
    try {
      const raw = await this.request<unknown>("/v1/account/cloud-usage", "GET");
      return normalizeCloudUsage(raw);
    } catch (err) {
      if (err instanceof RunAuthError) throw err;
      return null;
    }
  }

  /**
   * GET /v1/runs — list the caller's runs for the Activity view. The server
   * owns active/recent membership; we pass the filter through and render what
   * it returns (no client-side reclassification). Cookie auth; never
   * /v1/runtime/*.
   */
  async listRuns(
    opts: { status?: "active" | "recent" | "all"; limit?: number } = {},
  ): Promise<RunSummary[]> {
    const params = new URLSearchParams();
    if (opts.status) params.set("status", opts.status);
    if (opts.limit != null) params.set("limit", String(opts.limit));
    const qs = params.toString();
    const raw = await this.request<unknown>(`/v1/runs${qs ? `?${qs}` : ""}`, "GET");
    return normalizeRunList(raw);
  }

  /**
   * POST /v1/runs/:id/stop — owner-initiated stop (cookie session).
   *
   * Two-phase by design: for an active external-runner run the API moves the
   * run to `stopping` and returns `stopping: true`; the run only reaches
   * `stopped` after the runner acknowledges it actually terminated the workload
   * and proxy (observed via continued polling). A queued/unclaimed run is
   * `cancelled` outright (`stopping: false`); an already-terminal run is an
   * idempotent no-op. Never calls `/v1/runtime/*`.
   *
   * `{ force: true }` force-stops a run already wedged in `stopping` (the runner
   * is gone / never acked): the API cancels it immediately instead of leaving it
   * to the 30-minute stop-ack backstop. Returns `cancelled` (`stopping: false`).
   * The graceful (no-force) call sends no body and is unchanged.
   */
  async stopRun(
    id: string,
    opts: { force?: boolean } = {},
  ): Promise<StopRunResult> {
    const raw = await this.request<unknown>(
      `/v1/runs/${encodeURIComponent(id)}/stop`,
      "POST",
      opts.force ? { force: true } : undefined,
    );
    const r = (raw ?? {}) as Record<string, unknown>;
    return {
      id: typeof r["id"] === "string" ? (r["id"] as string) : id,
      status: typeof r["status"] === "string" ? (r["status"] as RunStatus) : "stopping",
      stopping: r["stopping"] === true,
    };
  }

  /**
   * POST /v1/preview-runs/:id/stop — stop an OWNER-LESS preview run (ato#1006).
   *
   * A preview run carries no owner (runs.user_id = NULL), so it is NOT stoppable
   * through the owner-scoped POST /v1/runs/:id/stop — that endpoint fails an
   * owner-less run closed (403) by design. Authorization here is the per-run
   * `controlToken` minted once at start (POST /v1/preview-runs), presented as the
   * `x-preview-control-token` request header (the account cookie is not the
   * authorization of record). Same two-phase / terminal-idempotent semantics as
   * `stopRun`: an active runner run goes to `stopping` until the runner acks; a
   * queued run is `cancelled`; a terminal run is a no-op. Never `/v1/runtime/*`.
   *
   * `{ keepalive: true }` is for stops fired while the document may be torn
   * down (unmount/navigation cleanup): without it the browser aborts the
   * in-flight request with the page, the run silently stays `ready`, and the
   * anon's single preview slot is held until the server's idle timeout — the
   * exact failure shape that hid the ato-api#245 CORS regression.
   */
  async stopPreviewRun(
    id: string,
    controlToken: string,
    opts: { keepalive?: boolean } = {},
  ): Promise<StopRunResult> {
    const raw = await this.request<unknown>(
      `/v1/preview-runs/${encodeURIComponent(id)}/stop`,
      "POST",
      undefined,
      { "x-preview-control-token": controlToken },
      opts,
    );
    const r = (raw ?? {}) as Record<string, unknown>;
    return {
      id: typeof r["id"] === "string" ? (r["id"] as string) : id,
      status: typeof r["status"] === "string" ? (r["status"] as RunStatus) : "stopping",
      stopping: r["stopping"] === true,
    };
  }

  /**
   * Read the live session continuation horizon. The per-run control token and
   * signed preview-browser cookie are both required; account auth is not a
   * substitute for either boundary.
   */
  async getPreviewContinuation(
    id: string,
    controlToken: string,
  ): Promise<PreviewContinuation> {
    const raw = await this.request<unknown>(
      `/v1/preview-runs/${encodeURIComponent(id)}/continuation`,
      "GET",
      undefined,
      { "x-preview-control-token": controlToken },
    );
    return normalizePreviewContinuation(raw);
  }

  /** Begin bounded grace or apply a completed sign-in/subscription grant. */
  async updatePreviewContinuation(
    id: string,
    controlToken: string,
    mutation: PreviewContinuationMutation,
  ): Promise<PreviewContinuation> {
    const raw = await this.request<unknown>(
      `/v1/preview-runs/${encodeURIComponent(id)}/continuation`,
      "POST",
      mutation,
      { "x-preview-control-token": controlToken },
    );
    return normalizePreviewContinuation(raw);
  }

  /**
   * POST /v1/runs/:id/consent — the owner's decision on a pending ExecutionPlan
   * consent (P4-A). The decision is bound to the EXACT `consentRef` the modal
   * showed (the API refuses a mismatch). `approve` keeps the run `needs_consent`
   * until the runner verifies + retries; `reject` fails it closed. Cookie
   * session; never `/v1/runtime/*`.
   */
  async decideConsent(
    id: string,
    decision: "approve" | "reject",
    consentRef: string,
  ): Promise<ConsentDecisionResult> {
    const raw = await this.request<unknown>(
      `/v1/runs/${encodeURIComponent(id)}/consent`,
      "POST",
      { decision, consent_ref: consentRef },
    );
    const r = (raw ?? {}) as Record<string, unknown>;
    return {
      id: typeof r["id"] === "string" ? (r["id"] as string) : id,
      decision,
      status: typeof r["status"] === "string" ? (r["status"] as RunStatus) : undefined,
      consentStatus:
        typeof r["consent_status"] === "string" ? (r["consent_status"] as string) : undefined,
    };
  }
}

/** Result of POST /v1/runs/:id/consent. */
export interface ConsentDecisionResult {
  id: string;
  decision: "approve" | "reject";
  /** Run status after the decision: still `needs_consent` on approve, `failed` on reject. */
  status?: RunStatus;
  consentStatus?: string;
}

/** Result of POST /v1/runs/:id/stop. */
export interface StopRunResult {
  id: string;
  status: RunStatus;
  /** True when the runner must still acknowledge teardown before `stopped`. */
  stopping: boolean;
}

export interface PollRunOptions {
  intervalMs?: number;
  /** Max polls before giving up. Default 120 (~3min at 1.5s). */
  maxPolls?: number;
  onUpdate?: (run: Run) => void;
  /** Abort polling (e.g. on component unmount). */
  signal?: { aborted: boolean };
  /** Injectable sleep for deterministic tests. */
  sleep?: (ms: number) => Promise<void>;
}

/**
 * Poll GET /v1/runs/:id until the run reaches a terminal status (ready/failed/
 * stopped), invoking `onUpdate` for each observed state. Resolves with the
 * final run, or the last seen run if aborted / max polls exceeded.
 */
export async function pollRun(
  client: RunsApiClient,
  id: string,
  opts: PollRunOptions = {},
): Promise<Run> {
  const intervalMs = opts.intervalMs ?? 1500;
  const maxPolls = opts.maxPolls ?? 120;
  const sleepFn = opts.sleep ?? sleep;

  let last: Run | null = null;
  for (let i = 0; i < maxPolls; i++) {
    if (opts.signal?.aborted) break;
    const run = await client.getRun(id);
    last = run;
    opts.onUpdate?.(run);
    if (isTerminalRunStatus(run.status)) return run;
    if (opts.signal?.aborted) break;
    await sleepFn(intervalMs);
  }
  if (last) return last;
  // No successful poll at all — surface as a protocol-level problem.
  throw new RunProtocolError(`run ${id} never returned a status`);
}
